import { useState, useMemo, useRef } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  Button,
  Collapse,
  Pagination,
  Avatar,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
  formatDistanceToNow,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { buildWhatsappLink, WHATSAPP_ICON_URL } from '../../../utils/whatsapp';
import { getIfoodErrorMessage } from '../../../utils/ifoodError';
import { getGoogleBusinessErrorMessage } from '../../../utils/googleBusinessError';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  getFeedbackReplySuggestions,
  getGoogleBusinessReplySuggestions,
} from '../../../services/api';
import { feedbackHeroTokens, getSentimentTone } from './feedbackHeroTokens';
import { getIfoodFeedbackReviewState } from './feedbackIfoodReviewState';

const agruparPorPeriodo = (feedbacks) => {
  const grupos = { Hoje: [], Ontem: [], 'Esta semana': [], 'Este mês': [], Antigos: [] };
  feedbacks.forEach((fb) => {
    const data = new Date(fb.criado_em);
    if (isToday(data)) grupos.Hoje.push(fb);
    else if (isYesterday(data)) grupos.Ontem.push(fb);
    else if (isThisWeek(data)) grupos['Esta semana'].push(fb);
    else if (isThisMonth(data)) grupos['Este mês'].push(fb);
    else grupos.Antigos.push(fb);
  });
  return grupos;
};

const formatarTelefone = (telefone) => {
  if (!telefone) return '';
  let clean = String(telefone).replace(/\D/g, '');
  if (clean.startsWith('55')) clean = clean.slice(2);
  clean = clean.replace(/^0+/, '');
  if (clean.length === 10) clean = clean.slice(0, 2) + '9' + clean.slice(2);
  return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
};

const formatarDataHora = (iso) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });

const getInitials = (name) => {
  if (!name) return '?';
  const cleaned = String(name).trim();
  if (!cleaned || /^n[aã]o identificado$/i.test(cleaned)) return null;
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatarTempoRelativo = (iso) => {
  if (!iso) return '';
  try {
    const data = new Date(iso);
    const diffMs = Date.now() - data.getTime();
    const diffDias = diffMs / (1000 * 60 * 60 * 24);
    if (diffDias < 0) return '';
    if (diffDias < 30) {
      return formatDistanceToNow(data, { addSuffix: true, locale: ptBR });
    }
    return '';
  } catch {
    return '';
  }
};

const formatarNota = (nota) => {
  const value = Number(nota || 0);
  if (!value || Number.isNaN(value)) return null;
  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
};

const getOrigem = (fb) =>
  fb.origem || fb.origem_nome || fb.canal || fb.fonte || fb.origem_pedido || '';

const formatarOrigem = (value) => {
  const origem = String(value || '').trim();
  if (!origem) return '';

  const labels = {
    atendabot: 'Atendabot',
    reservo: 'Reservo',
    feedback_form: 'Formulário',
    formulario: 'Formulário',
    ifood: 'iFood',
    google: 'Google',
    clube: 'Clube',
    clube_mesa: 'Clube Mesa',
    legado: 'Legado',
  };

  return labels[origem.toLowerCase()] || origem;
};

const IFOOD_STATUS_LABELS = {
  CREATED: 'Em processamento',
  NOT_REPLIED: 'Aguardando resposta',
  REPLIED: 'Resposta enviada',
  PUBLISHED: 'Publicada',
  INVALID: 'Inválida',
  DISCARDED: 'Descartada',
};

const NotaInline = ({ value, tone }) => {
  const { fonts, colors } = feedbackHeroTokens;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.35,
        px: 0.85,
        py: 0.4,
        borderRadius: 1.5,
        background: tone.bg,
        border: `1px solid ${tone.chip}`,
      }}
    >
      <Typography
        sx={{
          fontFamily: fonts.display,
          fontSize: '0.92rem',
          fontWeight: 800,
          color: colors.text,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: fonts.display,
          fontSize: '0.88rem',
          fontWeight: 800,
          color: tone.accent,
          lineHeight: 1,
        }}
      >
        ★
      </Typography>
    </Box>
  );
};

NotaInline.propTypes = {
  value: PropTypes.string.isRequired,
  tone: PropTypes.object.isRequired,
};

const ClassificationGroup = ({ items, sentimento, colors, fonts }) => {
  const isPositive = sentimento === 'positivo';
  const tone = isPositive ? colors.pos : colors.neg;
  const Icon = isPositive ? ThumbUpAltOutlinedIcon : ThumbDownAltOutlinedIcon;
  const titulo = isPositive ? 'Elogios' : 'Pontos a melhorar';

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" mb={0.75}>
        <Icon sx={{ fontSize: 14, color: tone.fg }} />
        <Typography
          sx={{
            fontFamily: fonts.body,
            fontSize: '0.7rem',
            fontWeight: 800,
            color: tone.fg,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
          }}
        >
          {titulo}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
        {items.map((c, i) => (
          <Chip
            key={i}
            size="small"
            label={`${(c.categoria && c.categoria.nome) || 'Categoria'} · ${c.intensidade}/5`}
            sx={{
              bgcolor: tone.chip,
              color: tone.fg,
              fontFamily: fonts.body,
              fontSize: '0.74rem',
              fontWeight: 600,
              borderRadius: 1,
              height: 24,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

ClassificationGroup.propTypes = {
  items: PropTypes.array.isRequired,
  sentimento: PropTypes.oneOf(['positivo', 'negativo']).isRequired,
  colors: PropTypes.object.isRequired,
  fonts: PropTypes.object.isRequired,
};

const FeedbackCard = ({
  fb,
  index,
  onMarkAtendido,
  onAnswerIfood,
  onAnswerGoogle,
  resolving = false,
}) => {
  const navigate = useNavigate();
  const { fonts, colors } = feedbackHeroTokens;

  const nomeBruto = fb.cliente_nome || '';
  const naoIdentificado = !nomeBruto || /^n[aã]o identificado$/i.test(nomeBruto);
  const nome = naoIdentificado ? 'Cliente não identificado' : nomeBruto;
  const telefone = formatarTelefone(fb.cliente_telefone);
  const whatsappLink = fb.cliente_whatsapp_link || buildWhatsappLink(fb.cliente_telefone);
  const origem = formatarOrigem(getOrigem(fb));
  const tone = getSentimentTone(fb.nota);
  const notaFormatada = formatarNota(fb.nota);
  const hasMessage = Boolean(fb.comentario && fb.comentario.trim().length > 0);
  const notaNumber = Number(fb.nota);
  const isNegative = Number.isFinite(notaNumber) && notaNumber > 0 && notaNumber <= 2;
  const isAtendido = fb.pendencia_atendida === true;
  const isGoogle = getOrigem(fb).toLowerCase() === 'google' || fb.google_live === true;
  const canMarkAtendido = !isGoogle && isNegative && !isAtendido && typeof onMarkAtendido === 'function';
  const {
    integration,
    isIfood,
    status: ifoodStatus,
    merchantReply,
    canAnswer: ifoodStatusAllowsAnswer,
  } = getIfoodFeedbackReviewState(fb);
  const canAnswerIfood = ifoodStatusAllowsAnswer && typeof onAnswerIfood === 'function';
  const canAnswerGoogle = isGoogle
    && Boolean(fb.google_review_id)
    && typeof onAnswerGoogle === 'function';

  const positivos = (fb.classificacoes || []).filter((c) => c.sentimento === 'positivo');
  const negativos = (fb.classificacoes || []).filter((c) => c.sentimento === 'negativo');
  const hasClassifications = positivos.length > 0 || negativos.length > 0;

  const initials = getInitials(nome);
  const tempoRelativo = formatarTempoRelativo(fb.criado_em);
  const dataAbsoluta = fb.criado_em ? formatarDataHora(fb.criado_em) : null;

  const handleView = () => {
    const sessionId = fb.sessao_id || fb.session_id || fb.cliente_id || fb.feedback_form_cliente_id;
    if (sessionId) navigate(`/feedback-hero/respostas/${sessionId}`);
  };

  return (
    <motion.div
      id={fb.id ? `feedback-${fb.id}` : undefined}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index, 8) * 0.04 }}
      whileHover={{ y: -2 }}
      style={{ scrollMarginTop: 90, borderRadius: 12 }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderLeft: `4px solid ${tone.accent}`,
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          width: '100%',
          maxWidth: 980,
          mx: 'auto',
          transition: 'border-color .25s ease, box-shadow .25s ease',
          '&:hover': {
            borderColor: `${tone.accent}55`,
            boxShadow: `0 12px 28px ${tone.accent}1F`,
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.25 } }}>
          {/* Header: identidade do cliente + sentimento/nota */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  fontSize: '0.82rem',
                  fontFamily: fonts.body,
                  fontWeight: 800,
                  bgcolor: naoIdentificado ? colors.surfaceAlt : tone.bg,
                  color: naoIdentificado ? colors.subtext : tone.fg,
                  border: `1px solid ${colors.softBorder}`,
                }}
              >
                {naoIdentificado || !initials ? (
                  <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />
                ) : (
                  initials
                )}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: naoIdentificado ? colors.subtext : colors.text,
                    fontStyle: naoIdentificado ? 'italic' : 'normal',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.2,
                  }}
                >
                  {nome}
                </Typography>
                {(tempoRelativo || dataAbsoluta) && (
                  <Tooltip title={dataAbsoluta || ''} arrow placement="top">
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: fonts.body,
                        fontSize: '0.74rem',
                        color: colors.subtext,
                        cursor: dataAbsoluta ? 'help' : 'default',
                      }}
                    >
                      {tempoRelativo || dataAbsoluta}
                      {origem ? ` · via ${origem}` : ''}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: '0 0 auto' }}>
              {notaFormatada && <NotaInline value={notaFormatada} tone={tone} />}
              <Chip
                label={tone.label}
                size="small"
                sx={{
                  bgcolor: tone.chip,
                  color: tone.fg,
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  borderRadius: 1.5,
                  fontSize: '0.72rem',
                  height: 26,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Stack>
          </Stack>

          {/* Comentário (quote-style) ou empty state inline */}
          <Box sx={{ mt: 1.75 }}>
            {hasMessage ? (
              <Box
                sx={{
                  pl: { xs: 1.5, sm: 2 },
                  borderLeft: `3px solid ${tone.chip}`,
                  py: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: { xs: '0.94rem', sm: '1rem' },
                    lineHeight: 1.55,
                    color: colors.text,
                    fontWeight: 500,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {fb.comentario}
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontFamily: fonts.body,
                  fontSize: '0.82rem',
                  color: colors.subtext,
                  fontStyle: 'italic',
                }}
              >
                Cliente não escreveu comentário.
              </Typography>
            )}
          </Box>

          {/* Classificações */}
          {hasClassifications && (
            <Box
              sx={{
                mt: 1.75,
                p: 1.5,
                borderRadius: 1.5,
                background: colors.surfaceAlt,
                border: `1px solid ${colors.softBorder}`,
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap divider={<Divider orientation="vertical" flexItem sx={{ borderColor: colors.softBorder, display: { xs: 'none', md: 'block' } }} />}>
                {positivos.length > 0 && (
                  <ClassificationGroup
                    items={positivos}
                    sentimento="positivo"
                    colors={colors}
                    fonts={fonts}
                  />
                )}
                {negativos.length > 0 && (
                  <ClassificationGroup
                    items={negativos}
                    sentimento="negativo"
                    colors={colors}
                    fonts={fonts}
                  />
                )}
              </Stack>
            </Box>
          )}

          {isIfood && (
            <Box
              sx={{
                mt: 1.75,
                p: 1.5,
                borderRadius: 1.5,
                background: '#FFF7F7',
                border: '1px solid #FBCFD3',
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <StorefrontRoundedIcon sx={{ fontSize: 17, color: '#C91524' }} />
                <Typography sx={{ fontFamily: fonts.body, fontWeight: 800, fontSize: '0.78rem', color: '#9F1420' }}>
                  iFood
                </Typography>
                <Chip
                  size="small"
                  label={IFOOD_STATUS_LABELS[ifoodStatus] || ifoodStatus || 'Status não informado'}
                  sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700 }}
                />
                {integration?.pedido_codigo && (
                  <Typography sx={{ fontFamily: fonts.body, fontSize: '0.74rem', color: colors.subtext }}>
                    Pedido {integration.pedido_codigo}
                  </Typography>
                )}
              </Stack>
              {merchantReply?.text && (
                <Typography
                  sx={{
                    mt: 1,
                    fontFamily: fonts.body,
                    fontSize: '0.82rem',
                    lineHeight: 1.45,
                    color: colors.text,
                  }}
                >
                  <strong>Resposta enviada:</strong> {merchantReply.text}
                </Typography>
              )}
            </Box>
          )}

          {isGoogle && (
            <Box
              sx={{
                mt: 1.75,
                p: 1.5,
                borderRadius: 1.5,
                background: '#F8FAFF',
                border: '1px solid #D7E3FC',
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <StorefrontRoundedIcon sx={{ fontSize: 17, color: '#1A73E8' }} />
                <Typography sx={{ fontFamily: fonts.body, fontWeight: 800, fontSize: '0.78rem', color: '#155FC0' }}>
                  Google Business Profile
                </Typography>
                <Chip
                  size="small"
                  label="Consultada ao vivo"
                  sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#E8F0FE', color: '#174EA6' }}
                />
              </Stack>
              {fb.google_reply?.comment && (
                <Typography
                  sx={{
                    mt: 1,
                    fontFamily: fonts.body,
                    fontSize: '0.82rem',
                    lineHeight: 1.45,
                    color: colors.text,
                  }}
                >
                  <strong>Resposta publicada:</strong> {fb.google_reply.comment}
                </Typography>
              )}
              <Typography sx={{ mt: 0.75, fontFamily: fonts.body, fontSize: '0.72rem', color: colors.subtext }}>
                Esta avaliação não é armazenada nem analisada automaticamente pela IA.
              </Typography>
            </Box>
          )}

          {/* Ações */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
          >
            <Box>
              {fb.feedback_form_cliente_id && (
                <Button
                  size="small"
                  onClick={handleView}
                  startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontFamily: fonts.body,
                    fontWeight: 600,
                    textTransform: 'none',
                    color: colors.subtext,
                    fontSize: '0.78rem',
                    px: 1,
                    minHeight: 32,
                    '&:hover': {
                      color: colors.text,
                      background: colors.surfaceAlt,
                    },
                  }}
                >
                  Ver avaliação completa
                </Button>
              )}
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ ml: 'auto', flexWrap: 'wrap', rowGap: 1 }}
            >
              {isNegative && isAtendido && (
                <Chip
                  icon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                  label="Atendido"
                  size="small"
                  sx={{
                    bgcolor: colors.pos.chip,
                    color: colors.pos.fg,
                    fontFamily: fonts.body,
                    fontWeight: 800,
                    borderRadius: 1.5,
                    height: 30,
                    '& .MuiChip-icon': { color: colors.pos.fg },
                  }}
                />
              )}

              {canMarkAtendido && (
                <Button
                  size="small"
                  onClick={() => onMarkAtendido(fb.id)}
                  disabled={resolving}
                  startIcon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    color: colors.neg.fg,
                    backgroundColor: colors.neg.chip,
                    border: `1px solid ${colors.neg.accent}33`,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    minHeight: 34,
                    px: 1.25,
                    py: 0.65,
                    '&:hover': {
                      backgroundColor: colors.neg.bg,
                      borderColor: `${colors.neg.accent}66`,
                    },
                  }}
                >
                  {resolving ? 'Marcando...' : 'Marcar como atendido'}
                </Button>
              )}

              {canAnswerIfood && (
                <Button
                  size="small"
                  onClick={() => onAnswerIfood(fb)}
                  startIcon={<ReplyRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    color: '#ffffff',
                    backgroundColor: '#EA1D2C',
                    borderRadius: 1.5,
                    textTransform: 'none',
                    minHeight: 34,
                    px: 1.5,
                    '&:hover': { backgroundColor: '#C91524' },
                  }}
                >
                  Responder no iFood
                </Button>
              )}

              {canAnswerGoogle && (
                <Button
                  size="small"
                  onClick={() => onAnswerGoogle(fb)}
                  startIcon={<ReplyRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    color: '#ffffff',
                    backgroundColor: '#1A73E8',
                    borderRadius: 1.5,
                    textTransform: 'none',
                    minHeight: 34,
                    px: 1.5,
                    '&:hover': { backgroundColor: '#155FC0' },
                  }}
                >
                  {fb.google_reply?.comment ? 'Editar resposta' : 'Responder no Google'}
                </Button>
              )}

              {whatsappLink && (
                <Button
                  component="a"
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={telefone ? `Falar com o cliente: ${telefone}` : 'Falar com o cliente'}
                  aria-label={telefone ? `Falar com o cliente pelo WhatsApp: ${telefone}` : 'Falar com o cliente pelo WhatsApp'}
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    color: '#ffffff',
                    backgroundColor: '#25D366',
                    borderRadius: 1.5,
                    textTransform: 'none',
                    minHeight: 34,
                    px: 1.5,
                    py: 0.65,
                    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.22)',
                    '&:hover': {
                      backgroundColor: '#1EBE5D',
                      boxShadow: '0 6px 16px rgba(37, 211, 102, 0.32)',
                    },
                    '& .whatsapp-button-icon': {
                      width: 16,
                      height: 16,
                      objectFit: 'contain',
                      flex: '0 0 auto',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={WHATSAPP_ICON_URL}
                    alt=""
                    aria-hidden="true"
                    className="whatsapp-button-icon"
                    sx={{ mr: 0.85 }}
                  />
                  Responder
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </motion.div>
  );
};

FeedbackCard.propTypes = {
  fb: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onMarkAtendido: PropTypes.func,
  onAnswerIfood: PropTypes.func,
  onAnswerGoogle: PropTypes.func,
  resolving: PropTypes.bool,
};

const FeedbackList = ({
  feedbacks = [],
  total = 0,
  page = 1,
  pageSize = 10,
  ordem = 'desc',
  attentionOnly = false,
  onPageChange,
  onClearAttentionMode,
  onMarkAtendido,
  onAnswerIfood,
  onAnswerGoogle,
  resolvingFeedbackId,
}) => {
  const { fonts, colors } = feedbackHeroTokens;
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [ifoodAnswerDialog, setIfoodAnswerDialog] = useState({ open: false, feedback: null });
  const [ifoodAnswerText, setIfoodAnswerText] = useState('');
  const [ifoodAnswerError, setIfoodAnswerError] = useState('');
  const [ifoodAnswerLoading, setIfoodAnswerLoading] = useState(false);
  const [googleAnswerDialog, setGoogleAnswerDialog] = useState({ open: false, feedback: null });
  const [googleAnswerText, setGoogleAnswerText] = useState('');
  const [googleAnswerError, setGoogleAnswerError] = useState('');
  const [googleAnswerLoading, setGoogleAnswerLoading] = useState(false);
  const [replySuggestions, setReplySuggestions] = useState([]);
  const [replySuggestionsLoading, setReplySuggestionsLoading] = useState(false);
  const [replySuggestionsError, setReplySuggestionsError] = useState('');
  const replySuggestionsCacheRef = useRef(new Map());
  const replySuggestionsRequestRef = useRef(0);
  const feedbacksList = useMemo(() => feedbacks || [], [feedbacks]);

  const grupos = useMemo(() => agruparPorPeriodo(feedbacksList), [feedbacksList]);
  const shouldGroup = ordem === 'desc' && feedbacksList.length >= 12;
  const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 1;
  const emptyMessage = attentionOnly
    ? 'Nenhuma pendência aberta encontrada com os filtros aplicados.'
    : 'Nenhum feedback encontrado com os filtros aplicados.';
  const isResolvingFeedback = (feedbackId) =>
    resolvingFeedbackId !== null
    && resolvingFeedbackId !== undefined
    && feedbackId !== null
    && feedbackId !== undefined
    && String(resolvingFeedbackId) === String(feedbackId);
  const loadReplySuggestions = async (feedback, { force = false } = {}) => {
    const isGoogleFeedback = feedback?.google_live === true
      || String(feedback?.origem || '').toLowerCase() === 'google';
    const feedbackId = isGoogleFeedback ? feedback?.google_review_id : feedback?.id;
    if (!feedbackId) return;
    const cacheKey = `${isGoogleFeedback ? 'google' : 'local'}:${feedbackId}`;
    const cached = replySuggestionsCacheRef.current.get(cacheKey);
    if (!force && Array.isArray(cached) && cached.length) {
      setReplySuggestionsLoading(false);
      setReplySuggestions(cached);
      setReplySuggestionsError('');
      return;
    }

    const requestId = replySuggestionsRequestRef.current + 1;
    replySuggestionsRequestRef.current = requestId;
    setReplySuggestionsLoading(true);
    setReplySuggestionsError('');
    if (force) setReplySuggestions([]);
    try {
      const result = isGoogleFeedback
        ? await getGoogleBusinessReplySuggestions(feedbackId)
        : await getFeedbackReplySuggestions(feedbackId);
      if (replySuggestionsRequestRef.current !== requestId) return;
      const suggestions = Array.isArray(result?.suggestions) ? result.suggestions : [];
      replySuggestionsCacheRef.current.set(cacheKey, suggestions);
      setReplySuggestions(suggestions);
      if (!suggestions.length) {
        setReplySuggestionsError('A IA não encontrou uma sugestão adequada para esta avaliação.');
      }
    } catch (error) {
      if (replySuggestionsRequestRef.current !== requestId) return;
      const fallback = 'Não foi possível gerar sugestões agora. Você ainda pode escrever a resposta normalmente.';
      setReplySuggestionsError(isGoogleFeedback
        ? getGoogleBusinessErrorMessage(error, fallback)
        : getApiErrorMessage(error, fallback));
    } finally {
      if (replySuggestionsRequestRef.current === requestId) {
        setReplySuggestionsLoading(false);
      }
    }
  };
  const openIfoodAnswer = (feedback) => {
    setIfoodAnswerDialog({ open: true, feedback });
    setIfoodAnswerText('');
    setIfoodAnswerError('');
    setReplySuggestionsLoading(false);
    setReplySuggestions([]);
    setReplySuggestionsError('');
    void loadReplySuggestions(feedback);
  };
  const closeIfoodAnswer = () => {
    if (ifoodAnswerLoading) return;
    replySuggestionsRequestRef.current += 1;
    setIfoodAnswerDialog({ open: false, feedback: null });
    setIfoodAnswerText('');
    setIfoodAnswerError('');
    setReplySuggestions([]);
    setReplySuggestionsError('');
    setReplySuggestionsLoading(false);
  };
  const submitIfoodAnswer = async () => {
    const text = ifoodAnswerText.trim();
    if (text.length < 10 || text.length > 300 || !ifoodAnswerDialog.feedback?.id) return;
    setIfoodAnswerLoading(true);
    setIfoodAnswerError('');
    try {
      await onAnswerIfood?.(ifoodAnswerDialog.feedback.id, text);
      setIfoodAnswerDialog({ open: false, feedback: null });
      setIfoodAnswerText('');
    } catch (error) {
      setIfoodAnswerError(getIfoodErrorMessage(
        error,
        'Não foi possível enviar a resposta ao iFood. Tente novamente.'
      ));
    } finally {
      setIfoodAnswerLoading(false);
    }
  };

  const openGoogleAnswer = (feedback) => {
    replySuggestionsRequestRef.current += 1;
    setGoogleAnswerDialog({ open: true, feedback });
    setGoogleAnswerText(String(feedback?.google_reply?.comment || ''));
    setGoogleAnswerError('');
    setReplySuggestionsLoading(false);
    setReplySuggestions([]);
    setReplySuggestionsError('');
    void loadReplySuggestions(feedback);
  };
  const closeGoogleAnswer = () => {
    if (googleAnswerLoading) return;
    replySuggestionsRequestRef.current += 1;
    setGoogleAnswerDialog({ open: false, feedback: null });
    setGoogleAnswerText('');
    setGoogleAnswerError('');
    setReplySuggestions([]);
    setReplySuggestionsError('');
    setReplySuggestionsLoading(false);
  };
  const submitGoogleAnswer = async () => {
    const text = googleAnswerText.trim();
    const reviewId = googleAnswerDialog.feedback?.google_review_id;
    if (!text || text.length > 4096 || !reviewId) return;
    setGoogleAnswerLoading(true);
    setGoogleAnswerError('');
    try {
      await onAnswerGoogle?.(reviewId, text);
      setGoogleAnswerDialog({ open: false, feedback: null });
      setGoogleAnswerText('');
    } catch (error) {
      setGoogleAnswerError(getGoogleBusinessErrorMessage(
        error,
        'Não foi possível publicar a resposta no Google. Tente novamente.'
      ));
    } finally {
      setGoogleAnswerLoading(false);
    }
  };

  return (
    <Grid item xs={12} id="feedbacks-list-section" sx={{ scrollMarginTop: 16 }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: { xs: 2, sm: 3 },
            background: colors.surface,
            border: `1px solid ${colors.softBorder}`,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: colors.surface,
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 2.5 },
              pb: { xs: 1.5, sm: 2 },
              borderBottom: `1px solid ${colors.softBorder}`,
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: fonts.display,
                    fontWeight: 700,
                    color: colors.text,
                    fontSize: { xs: '1.2rem', sm: '1.35rem' },
                  }}
                >
                  {attentionOnly ? 'Pendências abertas' : 'Feedbacks'}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: fonts.body, color: colors.subtext }}>
                  {feedbacksList.length === 0
                    ? emptyMessage
                    : `Mostrando ${feedbacksList.length} de ${total} ${total === 1 ? 'registro' : 'registros'}`}
                </Typography>
              </Box>
              {attentionOnly && (
                <Button
                  size="small"
                  onClick={onClearAttentionMode}
                  sx={{
                    fontFamily: fonts.body,
                    fontWeight: 800,
                    textTransform: 'none',
                    color: colors.text,
                    borderRadius: 1.5,
                    background: colors.surfaceAlt,
                    border: `1px solid ${colors.softBorder}`,
                    px: 1.5,
                    '&:hover': {
                      background: colors.softBorder,
                    },
                  }}
                >
                  Ver todos
                </Button>
              )}
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2.5, sm: 3 } }}>
            {shouldGroup ? (
              Object.entries(grupos).map(([titulo, lista]) =>
                lista.length > 0 ? (
                  <Box key={titulo} mb={{ xs: 3, sm: 4 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: fonts.display,
                          fontWeight: 700,
                          color: colors.text,
                          fontSize: { xs: '1rem', sm: '1.05rem' },
                        }}
                      >
                        {titulo}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={lista.length}
                          size="small"
                          sx={{
                            fontFamily: fonts.body,
                            background: colors.surfaceAlt,
                            border: `1px solid ${colors.softBorder}`,
                          }}
                        />
                        <Button
                          size="small"
                          onClick={() =>
                            setCollapsedGroups((prev) => ({
                              ...prev,
                              [titulo]: !prev[titulo],
                            }))
                          }
                          sx={{
                            fontFamily: fonts.body,
                            textTransform: 'none',
                            color: colors.subtext,
                          }}
                        >
                          {collapsedGroups[titulo] ? 'Expandir' : 'Recolher'}
                        </Button>
                      </Stack>
                    </Stack>

                    <Collapse in={!collapsedGroups[titulo]}>
                      <Stack spacing={{ xs: 2.5, sm: 3 }}>
                        {lista.map((fb, index) => (
                          <FeedbackCard
                            key={fb.id || `${titulo}-${index}`}
                            fb={fb}
                            index={index}
                            onMarkAtendido={onMarkAtendido}
                            onAnswerIfood={openIfoodAnswer}
                            onAnswerGoogle={openGoogleAnswer}
                            resolving={isResolvingFeedback(fb.id)}
                          />
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                ) : null
              )
            ) : (
              <Stack spacing={{ xs: 2.5, sm: 3 }}>
                {feedbacksList.map((fb, index) => (
                  <FeedbackCard
                    key={fb.id || index}
                    fb={fb}
                    index={index}
                    onMarkAtendido={onMarkAtendido}
                    onAnswerIfood={openIfoodAnswer}
                    onAnswerGoogle={openGoogleAnswer}
                    resolving={isResolvingFeedback(fb.id)}
                  />
                ))}
              </Stack>
            )}

            {totalPages > 1 && (
              <Stack alignItems="center" spacing={1} mt={{ xs: 3, sm: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => onPageChange?.(value)}
                  shape="rounded"
                  size="medium"
                />
                <Typography variant="caption" sx={{ fontFamily: fonts.body, color: colors.subtext }}>
                  Página {page} de {totalPages}
                </Typography>
              </Stack>
            )}
          </Box>
        </Paper>
      </motion.div>
      <Dialog open={ifoodAnswerDialog.open} onClose={closeIfoodAnswer} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Responder avaliação do iFood</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                {ifoodAnswerDialog.feedback?.cliente_nome || 'Cliente não identificado'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {ifoodAnswerDialog.feedback?.comentario || 'Avaliação sem comentário.'}
              </Typography>
            </Box>
            {ifoodAnswerError && <Alert severity="error">{ifoodAnswerError}</Alert>}
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                <Typography variant="body2" fontWeight={800}>Sugestões da IA</Typography>
              </Stack>
              {replySuggestionsLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Preparando duas respostas…
                  </Typography>
                </Stack>
              ) : replySuggestions.length > 0 ? (
                <Stack spacing={1}>
                  {replySuggestions.map((suggestion, index) => (
                    <Button
                      key={`${index}-${suggestion}`}
                      variant="outlined"
                      disabled={ifoodAnswerLoading}
                      onClick={() => setIfoodAnswerText(suggestion)}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        textTransform: 'none',
                        whiteSpace: 'normal',
                        lineHeight: 1.45,
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.15,
                      }}
                    >
                      {suggestion}
                    </Button>
                  ))}
                  <Typography variant="caption" color="text.secondary">
                    Escolha uma sugestão e edite como preferir antes de enviar.
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={0.75} alignItems="flex-start">
                  {replySuggestionsError && (
                    <Typography variant="caption" color="text.secondary">
                      {replySuggestionsError}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    startIcon={<AutoAwesomeRoundedIcon />}
                    onClick={() => loadReplySuggestions(ifoodAnswerDialog.feedback, { force: true })}
                    disabled={ifoodAnswerLoading}
                    sx={{ textTransform: 'none' }}
                  >
                    Gerar sugestões
                  </Button>
                </Stack>
              )}
            </Box>
            <TextField
              label="Resposta"
              value={ifoodAnswerText}
              onChange={(event) => setIfoodAnswerText(event.target.value)}
              multiline
              minRows={4}
              autoFocus
              disabled={ifoodAnswerLoading}
              inputProps={{ minLength: 10, maxLength: 300 }}
              helperText={`${ifoodAnswerText.trim().length}/300 caracteres · mínimo de 10`}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Não inclua dados pessoais, links, promoções ou linguagem ofensiva.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeIfoodAnswer} disabled={ifoodAnswerLoading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={submitIfoodAnswer}
            disabled={ifoodAnswerLoading || ifoodAnswerText.trim().length < 10 || ifoodAnswerText.trim().length > 300}
            startIcon={ifoodAnswerLoading ? <CircularProgress size={16} color="inherit" /> : <ReplyRoundedIcon />}
            sx={{ bgcolor: '#EA1D2C', '&:hover': { bgcolor: '#C91524' } }}
          >
            {ifoodAnswerLoading ? 'Enviando...' : 'Enviar ao iFood'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={googleAnswerDialog.open} onClose={closeGoogleAnswer} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {googleAnswerDialog.feedback?.google_reply?.comment
            ? 'Editar resposta no Google'
            : 'Responder avaliação do Google'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                {googleAnswerDialog.feedback?.cliente_nome || 'Usuário do Google'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {googleAnswerDialog.feedback?.comentario || 'Avaliação sem comentário.'}
              </Typography>
            </Box>
            {googleAnswerError && <Alert severity="error">{googleAnswerError}</Alert>}
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                <Typography variant="body2" fontWeight={800}>Sugestões da IA</Typography>
              </Stack>
              {replySuggestionsLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Preparando duas respostas…
                  </Typography>
                </Stack>
              ) : replySuggestions.length > 0 ? (
                <Stack spacing={1}>
                  {replySuggestions.map((suggestion, index) => (
                    <Button
                      key={`${index}-${suggestion}`}
                      variant="outlined"
                      disabled={googleAnswerLoading}
                      onClick={() => setGoogleAnswerText(suggestion)}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        textTransform: 'none',
                        whiteSpace: 'normal',
                        lineHeight: 1.45,
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.15,
                      }}
                    >
                      {suggestion}
                    </Button>
                  ))}
                  <Typography variant="caption" color="text.secondary">
                    Escolha uma sugestão e edite como preferir antes de publicar.
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={0.75}>
                  {replySuggestionsError && (
                    <Typography variant="caption" color="text.secondary">
                      {replySuggestionsError}
                    </Typography>
                  )}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                O comentário é usado pela IA somente para criar estas sugestões e não é salvo no FeedbackHero.
              </Typography>
            </Box>
            <TextField
              label="Resposta"
              value={googleAnswerText}
              onChange={(event) => setGoogleAnswerText(event.target.value)}
              multiline
              minRows={4}
              autoFocus
              disabled={googleAnswerLoading}
              inputProps={{ maxLength: 4096 }}
              helperText={`${googleAnswerText.length}/4096 caracteres`}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              A resposta será publicada diretamente na ficha conectada do Google.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeGoogleAnswer} disabled={googleAnswerLoading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={submitGoogleAnswer}
            disabled={googleAnswerLoading || !googleAnswerText.trim() || googleAnswerText.trim().length > 4096}
            startIcon={googleAnswerLoading ? <CircularProgress size={16} color="inherit" /> : <ReplyRoundedIcon />}
            sx={{ bgcolor: '#1A73E8', '&:hover': { bgcolor: '#155FC0' } }}
          >
            {googleAnswerLoading ? 'Publicando...' : 'Publicar no Google'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

FeedbackList.propTypes = {
  feedbacks: PropTypes.array,
  total: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  ordem: PropTypes.string,
  attentionOnly: PropTypes.bool,
  onPageChange: PropTypes.func,
  onClearAttentionMode: PropTypes.func,
  onMarkAtendido: PropTypes.func,
  onAnswerIfood: PropTypes.func,
  onAnswerGoogle: PropTypes.func,
  resolvingFeedbackId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default FeedbackList;
